import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Topics from "./pages/Topics";
import SessionConfig from "./pages/SessionConfig";
import Session from "./pages/Session";
import Results from "./pages/Results";
import Review from "./pages/Review";
import Flashcards from "./pages/Flashcards";
import Guides from "./pages/Guides";
import GuideView from "./pages/GuideView";
import Search from "./pages/Search";
import Account from "./pages/Account";
import Legal from "./pages/Legal";
import Contact from "./pages/Contact";
import DashboardLayout from "./components/DashboardLayout";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminQuestions from "./pages/admin/AdminQuestions";
import AdminQuestionEditor from "./pages/admin/AdminQuestionEditor";
import AdminReports from "./pages/admin/AdminReports";
import AdminComments from "./pages/admin/AdminComments";
import AdminGuides from "./pages/admin/AdminGuides";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAnalytics from "./pages/admin/AdminAnalytics";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
      <div className="w-8 h-8 rounded-md bg-primary animate-pulse" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function RequireSub({ children }) {
  const { user, hasAccess } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/pricing" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, isAdmin } = useAuth();
  if (user === null) return <Loading />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/onboarding" element={<RequireSub><Onboarding /></RequireSub>} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/legal" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/app" element={<RequireSub><DashboardLayout /></RequireSub>}>
              <Route index element={<Dashboard />} />
              <Route path="topics" element={<Topics />} />
              <Route path="topics/:slug" element={<SessionConfig />} />
              <Route path="flashcards" element={<Flashcards />} />
              <Route path="guides" element={<Guides />} />
              <Route path="guides/:id" element={<GuideView />} />
              <Route path="session/:id" element={<Session />} />
              <Route path="session/:id/results" element={<Results />} />
              <Route path="review" element={<Review />} />
              <Route path="search" element={<Search />} />
              <Route path="account" element={<Account />} />
            </Route>
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<AdminDashboard />} />
              <Route path="questions" element={<AdminQuestions />} />
              <Route path="questions/:id" element={<AdminQuestionEditor />} />
              <Route path="guides" element={<AdminGuides />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="comments" element={<AdminComments />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="analytics" element={<AdminAnalytics />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
